<template>
  <div>
    <div>
      <aha-modal
        @hide="hide()"
        :title="title_CreateNewFolder"
        action-position="justify-end"
      >
        <form @submit.prevent="handleCreateNewFolder()">
          <aha-input
            focus
            v-model="folderName"
            id="input_folder-name"
            :maxLength="limitFolderName"
          />
        </form>
        <template #action>
          <aha-link @click="hide()">
            <span class="font-bold">{{ $i18nPresenter.t("Cancel") }}</span>
          </aha-link>
          <aha-button
            :disabled="isCreatingFolder || folderName.length === 0"
            @click="handleCreateNewFolder()"
            icon-name="add"
            :submitting="isCreatingFolder"
            has-margin-left
          >
            <aha-spinner variant="white" v-if="isCreatingFolder" class="mr-1" />
            {{ $i18nPresenter.t("Create Folder") }}
          </aha-button>
        </template>
        <p class="pt-2 text-red-50" v-if="textError">
          {{ textError }}
        </p>
      </aha-modal>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

// api
import FolderApi from '@/api/folder'
import PresentationApi from '@/api/presentation'

// utils
import utils from '@/util'
import EventBus from '@/eventBus'

// constants
import constants from '@/constant'
import configs from '@/config'

const { MAX_FOLDER_DEPTH } = configs

export default {
  props: {
    presentationIds: {
      type: Array,
      default: () => [],
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      folderName: '',
      isCreatingFolder: false,
      limitFolderName: 100,
      textError: '',
      title_CreateNewFolder: this.$i18nPresenter.t('Create New Folder'),
    }
  },
  computed: {
    ...mapState({
      selectedFolderId: (state) => state.selections.selectedFolderId,
      workingMode: (state) => state.workingMode,
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
      currentFolderDepth: (state) => state.currentFolderDepth,
    }),
    isRouteFolderDetails() {
      return this.$route.name === 'FolderDetail'
    },
    isMaxFolderDepth() {
      return this.currentFolderDepth === MAX_FOLDER_DEPTH
    },
  },
  methods: {
    ...mapActions(['setErrorOnInternalServerAction']),
    trackEvent(label, value) {
      utils.mixpanelTracking(
        constants.eventCategories.PRESENTATION_LIST,
        label,
        value,
      )
    },
    handleCreateNewFolder() {
      utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'New > Create New Folder > Submit', `Folder name: ${this.folderName}`, null, {
        isTeamFolder: this.workingMode === constants.workspace.TEAM,
        role: this.currentUser.accountRole || null,
        teamId: this.currentAccount.id || null,
        parentFolderId: this.selectedFolderId,
        folderDepth: this.currentFolderDepth || 0,
      })

      if (this.folderName.trim() === '') {
        this.textError = this.$i18nPresenter.t('Please enter a name for folder.')
        return
      }

      this.isCreatingFolder = true
      if (this.isRouteFolderDetails) {
        this._handleCreateSubFolder()
      } else {
        this._handleCreateFolder()
      }
    },

    async _handleCreateSubFolder() {
      try {
        if (this.isMaxFolderDepth) return
        const result = await FolderApi.createSubFolder({
          parentFolderId: this.selectedFolderId,
          folderName: this.folderName.trim(),
        })
        this._processCreateFolderData(result)
        this.hide()
        EventBus.$emit('reload-presentations-and-folders')
      } catch (error) {
        if (error.body.message === 'folderName must be unique') {
          this.textError = this.$i18nPresenter.t('This folder name is already in use, please enter something else.')
        } else {
          utils.showToastError('Cannot create folder. Please check your connection and try again.')
        }
      }
      this.isCreatingFolder = false
    },

    async _handleCreateFolder() {
      try {
        const result = await FolderApi.createFolder({
          folderName: this.folderName.trim(),
        })
        this._processCreateFolderData(result)
      } catch (error) {
        if (error.body.message === 'folderName must be unique') {
          this.textError = this.$i18nPresenter.t('This folder name is already in use, please enter something else.')
        } else {
          utils.showToastError('Cannot create folder. Please check your connection and try again.')
        }

        this.trackEvent('New > Create New Folder > Failed', `Error: ${error.body.message}`)
      }

      this.isCreatingFolder = false
    },

    async _processCreateFolderData(result) {
      if (this.presentationIds.length) { // create new folder when moving presentation(s)
        await this.movePresentationToNewFolder(result)
      } else {
        utils.showToastSuccess(`${this.$i18nPresenter.t('Created folder')} "${this.folderName.trim()}"`)
      }

      this.hide()
      EventBus.$emit('reload-presentations-and-folders')
      utils.mixpanelTracking(constants.eventCategories.PRESENTATION_LIST, 'New > Create New Folder > Succeeded', `Folder name: ${this.folderName}`, null, {
        isTeamFolder: this.workingMode === constants.workspace.TEAM,
        isSelfCreatedTeam: this.currentAccount.createdFrom === 'presenter',
        role: this.currentUser.accountRole || null,
        teamId: this.currentAccount.id || null,
        folderId: (result && result.folder && result.folder.folderId) || null,
        parentFolderId: this.selectedFolderId,
        folderDepth: this.currentFolderDepth || 0,
      })
    },

    async movePresentationToNewFolder(result) {
      const payload = {
        presentationIds: this.presentationIds,
        presentation: { folderId: result.folder.folderId },
      }

      try {
        await PresentationApi.patchPresentations(payload)
      } catch (error) {
        utils.showToastError(this.$i18nPresenter.t('Move presentations failed'))
        this.trackEvent(
          'Presentation List > Move Presentation to new folder > Failed',
          error.body ? error.body.message : '',
        )
      }
    },
    hide() {
      this.$emit('hide')
    },
  },
}
</script>
